import React from "react";
import { getDiscardInfo } from "../services/getFunctions";
import { AlertContextType } from "../contexts/AlertContext";

import {
  SurveyState,
  SurveyStatusAndStws,
  SurveyTimeWindow,
} from "../types/surveyTypes";
import { getGermanTodayIso, formatCalendarDate } from "./dateHelpers";

/**
 * Checks if a date is between 2 times
 * @param stwStart Date as a string
 * @param stwEnd Date as a string
 * @param dateToTest Date as a string
 * @returns if dateToTest is between the other two dates
 */
const isWithinStw = (
  startDateStr: string,
  endDateStr: string,
  dateToTest: string,
): boolean => {
  try {
    if (!startDateStr || !endDateStr || !dateToTest) return false;

    // normalize the date to avoid timezone offset
    const normalize = (d: string) => d.split("T")[0];

    const start = normalize(startDateStr);
    const end = normalize(endDateStr);
    const test = normalize(dateToTest);

    return test >= start && test <= end;
  } catch (error) {
    console.warn("time checking error:", error);
    return false;
  }
};

const calculateSurveyStatus = (
  surveyStatus: SurveyState | undefined,
  stw: SurveyTimeWindow,
): { node: React.ReactNode; color: string } => {
  const now = getGermanTodayIso();

  if (stw.end < now && (!surveyStatus || !surveyStatus.finished)) {
    if (surveyStatus?.started_time) {
      return {
        node: (
          <>
            <span className="material-symbols-rounded">event_busy</span>{" "}
            Abgelaufen (Begonnen)
          </>
        ),
        color: "FireBrick",
      };
    } else {
      return {
        node: (
          <>
            <span className="material-symbols-rounded">event_busy</span>{" "}
            Abgelaufen (Nicht begonnen)
          </>
        ),
        color: "FireBrick",
      };
    }
  }

  if (surveyStatus) {
    if (surveyStatus.finished) {
      return {
        node: (
          <>
            <span className="material-symbols-rounded">event_available</span>{" "}
            Erledigt
          </>
        ),
        color: "green",
      };
    }

    if (
      surveyStatus.started_time &&
      stw.start <= surveyStatus.started_time &&
      now <= stw.end
    ) {
      return {
        node: (
          <>
            <span className="material-symbols-rounded">hourglass_top</span>{" "}
            Ausstehend (begonnen)
          </>
        ),
        color: "#104d92ff",
      };
    }
  }

  if (stw.start <= now && now <= stw.end) {
    return {
      node: (
        <>
          <span className="material-symbols-rounded">hourglass_empty</span>{" "}
          Ausstehend (Nicht begonnen)
        </>
      ),
      color: "gray",
    };
  }

  if (now < stw.start) {
    return {
      node: (
        <>
          <span className="material-symbols-rounded">event_upcoming</span> Nicht
          Verfügbar
        </>
      ),
      color: "gray",
    };
  }

  console.error("utils.calculateSurveyStatus: Unhandled case", {
    surveyStatus,
    stw,
  });
  //use Nicht Verfügbar as fallback instead of Fehler
  return {
    node: (
      <>
        <span className="material-symbols-rounded">event_upcoming</span> Nicht
        Verfügbar
      </>
    ),
    color: "gray",
  };
};

const calculateResultStatus = (
  surveyStatus: SurveyState | undefined,
  stw: SurveyTimeWindow,
): React.ReactNode => {
  if (!surveyStatus) {
    return (
      <>
        <span className="material-symbols-rounded">scan_delete</span> Nicht
        Verfügbar
      </>
    );
  }

  if (!surveyStatus.survey_session_id || !surveyStatus.finished) {
    return (
      <>
        <span className="material-symbols-rounded">scan_delete</span> Nicht
        Verfügbar
      </>
    );
  }

  switch (surveyStatus.condition_severity) {
    case "UNKNOWN":
      return (
        <>
          <span className="material-symbols-rounded">unknown_document</span>{" "}
          Unbekannt
        </>
      );
    case "LOW":
      return (
        <>
          <span className="material-symbols-rounded">draft</span> Unauffällig
        </>
      );
    case "MEDIUM":
      return (
        <>
          <span className="material-symbols-rounded">find_in_page</span>{" "}
          Auffällig
        </>
      );
    case "HIGH":
      return (
        <>
          <span className="material-symbols-rounded">find_in_page</span>{" "}
          Kritisch
        </>
      );
    case "ERROR":
    default:
      return (
        <>
          <span className="material-symbols-rounded">bolt</span> Fehler
        </>
      );
  }
};

// This method assumes that the first STW is PRE then it starts with INTERMEDIATE sessions
// If the start INTERMEDIATE session does not begin with index 1, this method needs to be changed
export const translateStwType = (type: string, index: number): string => {
  const labels = window.APP_CONFIG.REACT_APP_STW_TYPE_LABELS;
  const label = labels[type as keyof typeof labels];
  if (!label) return "Unknown Stw type";

  return label.replace("{index}", String(index));
};

const formatSurvey = (
  survey: {
    stw_type: string;
    finished: boolean;
    started: string;
    updated: string;
  },
  index: number,
): string =>
  `- ${translateStwType(survey.stw_type, index + 1)} (${survey.finished ? "Abgeschlossen" : "Nicht abgeschlossen"}): ` +
  `Startdatum: ${formatCalendarDate(survey.started)}, ` +
  `Aktualisierungsdatum: ${formatCalendarDate(survey.updated)}`;

export async function fetchAndShowDiscardInfo(
  patientId: string,
  admissionDate: Date,
  dischargeDate: Date,
  showAlertModal: AlertContextType["showAlertModal"],
): Promise<void> {
  try {
    const discardInfo = await getDiscardInfo(
      patientId,
      admissionDate,
      dischargeDate,
    );

    const discarded = discardInfo.surveys.filter((s) => s.discarded);
    if (discarded.length === 0) {
      showAlertModal("Durch diese Änderung werden keine Befragungen gelöscht");
      return;
    }
    const message = `Durch diese Änderung werden folgende Befragungen gelöscht:\n${discarded.map(formatSurvey).join("\n")}`;
    showAlertModal(message, "Achtung!", "Schließen", true);
  } catch {
    showAlertModal("Fehler beim Abrufen der Umfrage-Informationen.");
  }
}

export const translateGender = (gender: string): string => {
  const genderTranslations = {
    FEMALE: "Weiblich",
    MALE: "Männlich",
    DIVERSE: "Divers",
  };

  return genderTranslations[gender] || "Fehler";
};

// Zip survey states and survey time windows according to the start and end time
export const zipSurveyStatusAndStw = (
  stws: SurveyTimeWindow[],
  surveyStatuses: SurveyState[],
): SurveyStatusAndStws[] => {
  //defensive check: empty array
  if (!Array.isArray(stws)) {
    console.warn("stws is not an array", stws);
    return [];
  }

  if (!Array.isArray(surveyStatuses)) {
    console.warn("surveyStatuses is not an array:", surveyStatuses);
    surveyStatuses = [];
  }

  return stws.map((stw, index) => {
    let matchedSurveyStatus: SurveyState | undefined;

    //match according to type and time window
    if (surveyStatuses.length > 0) {
      matchedSurveyStatus = surveyStatuses.find(
        (surveyStatus) =>
          surveyStatus.stw_type === stw.type &&
          isWithinStw(stw.start, stw.end, surveyStatus.update_time),
      );
    }

    // Always create a full status object, regardless of whether a match was found
    const resultStatus = calculateResultStatus(matchedSurveyStatus, stw);
    const { node: surveyStatus, color: surveyStatusColor } =
      calculateSurveyStatus(matchedSurveyStatus, stw);
    const stwTypeDeutsch = translateStwType(stw.type, index);

    return {
      stw_start: stw.start,
      stw_end: stw.end,
      surveyState: {
        stw_type: stw.type,
        survey_session_id: matchedSurveyStatus?.survey_session_id ?? "",
        started_time: matchedSurveyStatus?.started_time ?? "",
        update_time: matchedSurveyStatus?.update_time ?? "",
        finished: matchedSurveyStatus?.finished ?? false,
        report_viewed: matchedSurveyStatus?.report_viewed ?? false,
        condition_severity:
          matchedSurveyStatus?.condition_severity ?? "UNKNOWN",
      },
      survey_status: surveyStatus,
      result_status: resultStatus,
      stw_type_deutsch: stwTypeDeutsch,
      survey_status_color: surveyStatusColor,
    };
  });
};
